import { useContext, useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { EarthLoader } from '../components/loaders/EarthLoader';
import { Navigation } from '../components/navigation';
import { PotreeSourcesContext } from '../contexts/PotreeSourcesContext';
import { UserContext } from '../contexts/UserContext';
import { usePotreeSources } from '../hooks/usePotreeSources';
import { useCurrentUserQuery } from '../types/graphqlTypes';
import './app.css';
import { PermissionDeniedOverlay } from '../components/PermissionDeniedOverlay';
import { PermissionDeniedProvider, usePermissionDenied } from '../contexts/PermissionDeniedContext';
import { LanguageContext } from '../translation/src/context/LanguageContext';
import { useDarkModeTheme, usePreloadUserSettings } from '../modules/Properties/integration/UserSettingsIntegration';
import { loginPathFor } from '../utils/loginRedirect';

const SyncLanguage: React.FC = () => {
  const { language: userLanguage } = useContext(UserContext);
  const { setLanguage, language: currentLanguage } = useContext(LanguageContext);
  // Sync server language to LanguageProvider on load
  useEffect(() => {
    if (userLanguage && userLanguage !== currentLanguage) {
      setLanguage(userLanguage);
    }
  }, [userLanguage, currentLanguage, setLanguage]);

  return null;
};

const PermissionDeniedModalHandler: React.FC = () => {
  const { permissionDeniedState, hidePermissionDenied } = usePermissionDenied();

  return <PermissionDeniedOverlay open={permissionDeniedState} onClose={hidePermissionDenied} />;
};

function App() {
  const { data: userData, loading: userDataLoading } = useCurrentUserQuery();
  const [lastSelectedOrganisation, setLastSelectedOrganisation] = useLocalStorage('lastSelectedOrganisation', '');
  const potreeSourcesLoaded = usePotreeSources();
  const navigate = useNavigate();
  const location = useLocation();
  const { organisationId, projectId, definitionId, templateId } = useParams();

  useEffect(() => {
    if (userDataLoading) return;
    // Remember where the visitor was heading (a shared project link, a notification target…) so
    // the login page can bring them back there instead of the home page.
    if (!userData?.currentUser) navigate(loginPathFor(location), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- location is read once, when the redirect happens
  }, [navigate, userData?.currentUser, userDataLoading]);

  useEffect(() => {
    if (!userData?.currentUser) return;
    if (location.pathname === '/' || location.pathname === '/organisations' || location.pathname === '/organisations/') {
      let organisationId = lastSelectedOrganisation;
      const organisation = organisationId && userData?.currentUser?.organisations.find((organisation) => organisation.id === organisationId);

      const isValidOrganisation = organisation && organisation?.myPermissions.length > 0;
      if (!isValidOrganisation || !organisationId) organisationId = userData?.currentUser?.organisations[0]?.id;
      if (organisationId) {
        navigate(`/organisations/${organisationId}/projects`);
      } else {
        navigate(`/no-organisation`);
      }
    }
  }, [lastSelectedOrganisation, location.pathname, navigate, userData?.currentUser]);

  useEffect(() => {
    // Skip when unchanged: useLocalStorage's setter dispatches a 'local-storage' event
    // that forces re-renders in every component reading the same key (App, Navigation, ...).
    // On org switch this cascade was costing two extra full re-renders.
    if (organisationId && organisationId !== lastSelectedOrganisation) setLastSelectedOrganisation(organisationId);
  }, [organisationId, lastSelectedOrganisation, setLastSelectedOrganisation]);

  // Theme is driven by the `user_dark_mode` reactive property.
  useDarkModeTheme();
  // The user's settings, loaded on every page (not only inside a project).
  usePreloadUserSettings();

  // Only the first load shows the loader. A refetch of the user (after an invite, a grant: whatever refetches
  // the active queries) reports loading too — Apollo Client 4 does by default — and must not unmount the app.
  if ((userDataLoading && !userData) || !userData?.currentUser)
    return (
      <div className="flex items-center-justify-center">
        <EarthLoader />
      </div>
    );

  return (
    <UserContext.Provider value={userData.currentUser}>
      <PotreeSourcesContext.Provider value={{ isLoaded: potreeSourcesLoaded }}>
        <PermissionDeniedProvider>
          <SyncLanguage />
          <div className="flex w-full h-full overflow-auto">
            {/* Full-screen workspaces (a project, the object / template editor) have no sidebar. */}
            {!projectId && !definitionId && !templateId && <Navigation />}
            <div className="flex flex-grow overflow-auto">
              <Outlet />
            </div>
          </div>
          <PermissionDeniedModalHandler />
        </PermissionDeniedProvider>
      </PotreeSourcesContext.Provider>
    </UserContext.Provider>
  );
}

export default App;
